import React, {useMemo, useState} from 'react';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import ArrTreeView from '../../../../common/arr-tree-view/ArrTreeView';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import {useGetFolderListView} from '../../../../../hooks/document/useDocument';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {IFolderListViewResponse} from '../../../../../api/types/document.type';
import './Folder.scss';
import {t} from 'i18next';

export const Folder = () => {
  const activeDocumentObj = useSelector(
    (state: RootState) => state.docView.activeDoc
  );
  const activeDocumentId = Number(activeDocumentObj?.documentId);
  const isValidActiveDocumentId =
    Number.isFinite(activeDocumentId) && activeDocumentId > 0;
  const [selectedItems, setSelectedItems] = useState<string[] | null>(null);

  const {data: folderListData, isPending: isLoading} = useGetFolderListView(
    activeDocumentId,
    isValidActiveDocumentId
  );

  const prepareTreeData = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    return nodes.map((node) => {
      const hasChildren = node.children && node.children.length > 0;

      const preparedNode: TreeViewBaseItem<ExtendedTreeItemProps> = {
        ...node,
        hasChildren: hasChildren,
        isChildrenLoaded: hasChildren,
      };

      if (hasChildren && node.children) {
        preparedNode.children = prepareTreeData(node.children);
      }

      return preparedNode;
    });
  };

  const transformFlatTreeToNested = (
    flatData: IFolderListViewResponse[] | undefined | null
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    if (!flatData || !Array.isArray(flatData) || flatData.length === 0) {
      return [];
    }

    const nodeMap = new Map<string, TreeViewBaseItem<ExtendedTreeItemProps>>();
    const rootNodes: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];
    flatData.forEach((item) => {
      if (!item || typeof item !== 'object' || !item.id) {
        return;
      }
      const label = item.text;
      const fileType = item.isGroup === true ? 'folder' : 'tick';

      const nodeData = {
        id: String(item.id),
        label: label,
        fileType: fileType,
        hasChildren: false,
        isChildrenLoaded: false,
        disabled: false,
        ...(item.saveSearchSecurityGroupId !== undefined && {
          saveSearchSecurityGroupId: item.saveSearchSecurityGroupId,
        }),
      };
      const node = nodeData as TreeViewBaseItem<ExtendedTreeItemProps>;
      nodeMap.set(String(item.id), node);
    });

    flatData.forEach((item) => {
      if (!item || typeof item !== 'object' || !item.id) {
        return;
      }

      const nodeId = String(item.id);
      const node = nodeMap.get(nodeId);
      if (!node) return;

      const parentId = item.parent;
      if (
        parentId === '#' ||
        parentId === null ||
        parentId === undefined ||
        parentId === ''
      ) {
        rootNodes.push(node);
      } else {
        const parent = nodeMap.get(String(parentId));
        if (parent) {
          parent.children ??= [];
          parent.children.push(node);
        } else {
          rootNodes.push(node);
        }
      }
    });

    return prepareTreeData(rootNodes);
  };

  const treeData = useMemo(() => {
    return transformFlatTreeToNested(folderListData);
  }, [folderListData]);
  return (
    <div className="doc-details-folder" data-testid={`folder-div`}>
      {isLoading ? (
        <ArrLoadingSpinner />
      ) : (
        <ArrTreeView
          itemsList={treeData}
          setSelectedItem={setSelectedItems}
          treeSelectedItems={selectedItems}
          isShowCountLabel={false}
          handleNoResultMessage={t('docView.headers.docDetails.folder.noData')}
          leafTickExtraPadding={true}
        />
      )}
    </div>
  );
};

export default Folder;
